// src/pages/Success.jsx

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@web/components/ui/dialog';
import { PublicPaymentSuccessData } from '@shared/types';
import { useEffect, useState } from 'react';

import { Check } from 'lucide-react';

export function PublicPaymentSuccess({
  defaultValues,
}: {
  internalPaymentToken: string;
  defaultValues: PublicPaymentSuccessData;
}) {
  const [payments, setPayments] = useState(defaultValues.previousPayments);
  const [totalPaid, setTotalPaid] = useState(defaultValues.totalPaid);
  const [remainingBalance, setRemainingBalance] = useState(
    defaultValues.contractValue - defaultValues.totalPaid,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [latestPayment, setLatestPayment] = useState(
    defaultValues.previousPayments[0],
  );
  const [totalContractValue, setTotalContractValue] = useState(
    defaultValues.contractValue,
  );

  console.log('defaultValues', defaultValues);

  // Fetch payment data (Replace with actual API call)
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        // Simulate API call delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setPayments(defaultValues.previousPayments);

        // Calculate total paid
        setTotalPaid(defaultValues.totalPaid);

        // Calculate remaining balance
        setRemainingBalance(
          defaultValues.contractValue - defaultValues.totalPaid,
        );

        setTotalContractValue(defaultValues.contractValue);

        // Identify the latest payment based on the date
        const sortedPayments = [...defaultValues.previousPayments].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setLatestPayment(sortedPayments[0]);
      } catch (err) {
        setError('Failed to load payment data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    void fetchPayments();
  }, [defaultValues]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-4">
      <Dialog open={true}>
        <DialogContent className="z-50 mx-auto h-[98dvh] max-w-3xl overflow-auto overflow-y-auto rounded-lg bg-white p-6 shadow-lg">
          <DialogHeader className="flex flex-col items-center">
            {/* Success Icon */}
            <Check className="stroke-primary mb-2 h-16 w-16" />

            {/* Success Message */}
            <DialogTitle className="mb-6 flex flex-col items-center text-2xl font-semibold">
              Payment Successful!
            </DialogTitle>

            <DialogDescription className="mb-6 text-center text-gray-600">
              Thank you for your payment. Please find the detailed information
              about your payments below.
            </DialogDescription>
          </DialogHeader>

          <div className="">
            {/* Loading State */}
            {loading && (
              <p className="text-gray-700">Loading payment details...</p>
            )}

            {/* Error State */}
            {error && <p className="text-red-600">{error}</p>}

            {/* Payments Table */}
            {!loading && !error && (
              <>
                <div className="mb-6 w-full">
                  <h2 className="mb-4 text-xl font-semibold">Your Payments</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border bg-white">
                      <thead>
                        <tr>
                          <th className="border px-4 py-2">Amount ($)</th>
                          <th className="border px-4 py-2">Date</th>
                          {/* <th className="border px-4 py-2">Reference Code</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment, index) => (
                          <tr
                            key={index}
                            className={`text-center ${
                              payment.paymentId === latestPayment.paymentId
                                ? 'bg-green-100'
                                : ''
                            }`}
                          >
                            <td className="border px-4 py-2">
                              {payment.amount.toFixed(2)}
                            </td>
                            <td className="border px-4 py-2">{payment.date}</td>
                            {/* <td className="flex items-center justify-center space-x-2 border px-4 py-2">
                              <span className="font-medium">
                                {payment.paymentId}
                              </span>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary Section */}
                <div className="mb-6 w-full">
                  <h2 className="mb-4 text-xl font-semibold">Summary</h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Total Paid */}
                    <div className="rounded-lg bg-green-50 p-4 shadow">
                      <h3 className="text-lg font-medium">Total Paid</h3>
                      <p className="text-2xl font-semibold text-green-600">
                        ${totalPaid.toFixed(2)}
                      </p>
                    </div>

                    {/* Remaining Balance */}
                    <div className="rounded-lg bg-yellow-50 p-4 shadow">
                      <h3 className="text-lg font-medium">Remaining Balance</h3>
                      <p className="text-2xl font-semibold text-yellow-600">
                        ${remainingBalance.toFixed(2)}
                      </p>
                    </div>

                    {/* Total Contract Value */}
                    <div className="rounded-lg bg-blue-50 p-4 shadow">
                      <h3 className="text-lg font-medium">
                        Total Contract Value
                      </h3>
                      <p className="text-2xl font-semibold text-blue-600">
                        ${totalContractValue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Latest Payment Reference Code */}

                {/* <div className="mb-6 w-full">
                  <h2 className="mb-4 text-xl font-semibold">
                    Latest Payment Reference Code
                  </h2>
                  <div className="w-full rounded-lg bg-purple-50 p-4 text-center shadow">
                    <p className="mb-2 text-lg text-gray-700">
                      Use the following reference code for any inquiries related
                      to your latest payment:
                    </p>
                    <p className="text-2xl font-bold text-purple-600">
                      {latestPayment.paymentId}
                    </p>
                  </div>
                </div> */}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
